/* eslint-disable react/prop-types */
import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { actions, clearUser, createUser, editUser, getUser, inviteUser } from "../../../data/userSlice"
import { useEffect, useState } from "react"
import { validEmailRegex } from "../../../constant/utils"
import { getRoles } from "../../../data/enumSlice"
import { css } from "@emotion/react"
import Loader from "../../../components/Loader"
import { PatternFormat } from "react-number-format"

const componentStyled = css`
    .footer-button-container {
        display: flex;
        justify-content: flex-end;
    }

    .footer-error-container {
        display: flex;
        justify-content: flex-end;
        color: red;
        margin-top: 6px;
    }
`

const { updateUser } = actions
const AddUserModal = ({ showModal, handleCloseModal, currentUser, getUsersList, fetchStatus, setActivePage }) => {
    const dispatch = useDispatch()
    const { user } = useSelector((state) => state.user)
    let { roles } = useSelector((state) => state.enums)
    const { role } = useSelector((state) => state.authentication)
    const [updatedRoles, setUpdatedRoles] = useState([])

    const [errors, setErrors] = useState({
        firstName: null,
        lastName: null,
        role: null,
        email: null,
        phoneNumber: null,
        standardMessage: null
    })

    useEffect(() => {
        !roles?.length && dispatch(getRoles())
    }, [])

    useEffect(() => {
        let roleIndex = roles?.findIndex((_role) => _role?.value === role)
        let newRoles = [...roles]
        if (roleIndex !== -1) {
            newRoles = newRoles.slice(0, roleIndex + 1)
        }
        setUpdatedRoles(newRoles)
    }, [role, roles])

    const update = (e) => {
        const { name, type, value, checked } = e.currentTarget
        let updatedValue

        if (type === "number") {
            updatedValue = parseFloat(value)
        } else if (type === "checkbox") {
            updatedValue = checked
        } else {
            updatedValue = value
        }

        dispatch(updateUser({ field: name, value: updatedValue }))

        if (!!errors[name]) {
            setErrors((preErrors) => ({
                ...preErrors,
                [name]: null
            }))
        }
    }

    useEffect(() => {
        currentUser?.id && dispatch(getUser(currentUser?.id))
    }, [currentUser])

    useEffect(() => {
        if (!currentUser?.id && showModal) {
            dispatch(updateUser({ field: "active", value: true }))
            dispatch(updateUser({ field: "sendInvite", value: true }))
        }
        if (!showModal) {
            dispatch(clearUser())
            setErrors({
                firstName: null,
                lastName: null,
                role: null,
                email: null,
                phoneNumber: null,
                standardMessage: null
            })
        }
    }, [currentUser, showModal])

    const onSubmit = () => {
        if (
            !user?.firstName ||
            !user?.email ||
            !validEmailRegex.test(user?.email) ||
            !user?.role ||
            !user?.phoneNumber ||
            !user?.lastName
        ) {
            setErrors({
                firstName: !user?.firstName ? "Please enter first name" : "",
                lastName: !user?.lastName ? "Please enter last name" : "",
                email: !user?.email || !validEmailRegex.test(user?.email) ? "Please enter a valid email" : "",
                role: !user?.role ? "Please select user role" : "",
                phoneNumber: !user?.phoneNumber ? "Please enter phone number" : ""
            })
            return false
        }

        const { sendInvite, ...userPayload } = user
        if (currentUser?.id) {
            dispatch(editUser({ ...userPayload })).then(({ payload }) => {
                if (payload === undefined) {
                    setErrors((preErrors) => ({
                        ...preErrors,
                        standardMessage: "Unauthorized: Please authenticate to access this resource"
                    }))
                } else if (payload?.succeeded) {
                    if (!!sendInvite) {
                        dispatch(inviteUser(payload?.id))
                    }
                    getUsersList().then(({ payload }) => {
                        setActivePage(0)
                        handleCloseModal()
                    })
                } else if (!!payload?.errorMessage) {
                    setErrors((preErrors) => ({
                        ...preErrors,
                        standardMessage: payload?.errorMessage
                    }))
                }
            })
        } else {
            dispatch(
                createUser({
                    ...userPayload
                })
            ).then(({ payload }) => {
                if (payload === undefined) {
                    setErrors((preErrors) => ({
                        ...preErrors,
                        standardMessage: "Unauthorized: Please authenticate to access this resource"
                    }))
                } else if (payload?.succeeded) {
                    if (!!sendInvite) {
                        dispatch(inviteUser(payload?.id))
                    }
                    getUsersList().then(({ payload }) => {
                        setActivePage(0)
                        handleCloseModal()
                    })
                } else if (!!payload?.errorMessage) {
                    setErrors((preErrors) => ({
                        ...preErrors,
                        standardMessage: payload?.errorMessage
                    }))
                }
            })
        }
    }

    return (
        <Modal
            show={showModal}
            onHide={handleCloseModal}
            centered>
            <Modal.Header closeButton>
                <Modal.Title>{currentUser?.id ? "Edit User" : "Add User"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Loader show={fetchStatus === "loading"} />
                <Form>
                    <Form.Group controlId="formFirstName">
                        <Form.Label>
                            First Name<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            className={`form-control-sm ${errors?.firstName ? "error" : ""}`}
                            name="firstName"
                            value={user?.firstName || ""}
                            onChange={update}
                        />
                        {errors?.firstName && <Form.Text className="text-danger">{errors?.firstName}</Form.Text>}
                    </Form.Group>
                    <Form.Group
                        controlId="formLastName"
                        className="mt-2">
                        <Form.Label>
                            Last Name<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            name="lastName"
                            className={`form-control-sm ${errors?.lastName ? "error" : ""}`}
                            value={user?.lastName || ""}
                            onChange={update}
                        />
                        {errors?.lastName && <Form.Text className="text-danger">{errors?.lastName}</Form.Text>}
                    </Form.Group>
                    <Form.Group
                        controlId="formPhoneNumber"
                        className="mt-2">
                        <Form.Label>
                            Phone Number<span className="text-danger">*</span>
                        </Form.Label>
                        <PatternFormat
                            format="(###) ###-####"
                            allowEmptyFormatting
                            mask="_"
                            name="phoneNumber"
                            className={`form-control form-control-sm pattern-format ${
                                errors?.phoneNumber ? "error" : ""
                            }`}
                            value={user?.phoneNumber || ""}
                            onChange={update}
                        />
                        {errors?.phoneNumber && <Form.Text className="text-danger">{errors?.phoneNumber}</Form.Text>}
                    </Form.Group>
                    <Form.Group
                        controlId="formEmail"
                        className="mt-2">
                        <Form.Label>
                            Email<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="email"
                            name="email"
                            className={`form-control-sm ${errors?.email ? "error" : ""}`}
                            value={user?.email || ""}
                            onChange={update}
                        />
                        {errors?.email && <Form.Text className="text-danger">{errors?.email}</Form.Text>}
                    </Form.Group>
                    <Form.Group
                        controlId="userRol"
                        className="mt-2">
                        <Form.Label>
                            Role<span className="text-danger">*</span>
                        </Form.Label>
                        {user?.role && updatedRoles.findIndex((_role) => _role.value === user?.role) === -1 ? (
                            <Form.Control
                                type="text"
                                className="form-control-sm"
                                value={user?.role}
                                disabled
                            />
                        ) : (
                            <Form.Select
                                value={user?.role || ""}
                                name="role"
                                className={`form-select-sm ${errors?.role ? "error" : ""}`}
                                onChange={update}>
                                <option value={""}>--- Select Role ---</option>
                                {updatedRoles?.map((_role, index) => (
                                    <option
                                        key={_role?.value}
                                        value={_role?.value}>
                                        {_role?.name}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                        {errors?.role && (
                            <>
                                <Form.Text className="text-danger">{errors?.role}</Form.Text>
                                <br />
                            </>
                        )}
                        <Form.Text className="text-muted">Admin can see and edit all applications</Form.Text>
                    </Form.Group>
                    {currentUser?.id && (
                        <Row sm={12}>
                            <Col sm={2}>
                                <Form.Group
                                    controlId="formActive"
                                    className="mt-2">
                                    <Form.Check
                                        type="checkbox"
                                        label="Active"
                                        name="active"
                                        checked={user?.active === true}
                                        onChange={update}
                                    />
                                </Form.Group>
                            </Col>
                            <Col sm={4}>
                                <Form.Group
                                    controlId="formActive"
                                    className="mt-2">
                                    <Form.Check
                                        type="checkbox"
                                        label="Send Invite"
                                        name="sendInvite"
                                        checked={user?.sendInvite === true}
                                        onChange={update}
                                        disabled={!user?.active || user?.active === false}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer css={componentStyled}>
                <Col sm={12}>
                    <div className="footer-button-container">
                        <Button
                            className="btn-sm me-2"
                            variant="secondary"
                            onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button
                            className="btn-sm"
                            variant="primary"
                            onClick={onSubmit}>
                            Save
                        </Button>
                    </div>
                    {!!errors?.standardMessage && (
                        <div className="footer-error-container">{errors?.standardMessage}</div>
                    )}
                </Col>
            </Modal.Footer>
        </Modal>
    )
}

export default AddUserModal
